
import { useScrollReveal } from '../../../hooks/useScrollReveal';

const steps = [
  {
    number: '01',
    title: 'AI Audit',
    description: 'We assess operations, workflows, data readiness, and opportunity alignment.',
    subtitle: 'No assumptions. No hype. Only measurable potential.',
    image: 'https://public.readdy.ai/ai/img_res/4900b6c58007766fbed875b3850c9e61.jpg',
  },
  {
    number: '02',
    title: 'Strategic Roadmap',
    description: 'We prioritize initiatives based on ROI, risk profile, and implementation feasibility.',
    subtitle: 'Every recommendation is business-case driven.',
    image: 'https://readdy.ai/api/search-image?query=Abstract%20minimal%20dark%20composition%20representing%20strategic%20planning%20with%20interconnected%20pathway%20lines%20and%20milestone%20nodes%20on%20deep%20navy%20background%20with%20subtle%20steel%20blue%20glow%20accents%20cinematic%20premium%20corporate%20aesthetic%20clean%20modern&width=600&height=400&seq=step002&orientation=landscape',
  },
  {
    number: '03',
    title: 'Precision Execution',
    description: 'We implement what matters. System integration, workflow alignment, adoption planning, and performance tracking.',
    subtitle: 'Measurable outcomes. Disciplined delivery.',
    image: 'https://readdy.ai/api/search-image?query=Abstract%20minimal%20dark%20composition%20representing%20precision%20execution%20with%20converging%20geometric%20lines%20forming%20a%20sharp%20focal%20point%20on%20deep%20navy%20background%20with%20subtle%20steel%20blue%20light%20rays%20cinematic%20premium%20corporate%20aesthetic%20clean%20modern&width=600&height=400&seq=step003&orientation=landscape',
  },
];

export default function OurApproach() {
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal(0.2);

  return (
    <section id="approach" className="py-32 md:py-40 bg-[#0B0D10] relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div ref={headerRef} className={`text-center mb-24 transition-all duration-1000 ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <p className="text-sm tracking-[0.25em] uppercase text-[#3A5F8C] mb-4 font-medium">Our Process</p>
          <h2 className="text-4xl md:text-5xl lg:text-[56px] font-bold text-[#F5F7FA] tracking-tight leading-[1.1]">
            Clarity. Strategy. Execution.
          </h2>
        </div>

        <div className="space-y-32">
          {steps.map((step, index) => (
            <StepRow key={index} step={step} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}

function StepRow({ step, index }: { step: typeof steps[0]; index: number }) {
  const { ref, isVisible } = useScrollReveal(0.15);
  const isReversed = index % 2 !== 0;

  return (
    <div
      ref={ref}
      className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
    >
      <div className={`${isReversed ? 'lg:order-2' : ''}`}>
        <div className="relative w-full h-[360px] rounded-2xl overflow-hidden">
          <img
            src={step.image}
            alt={step.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B0D10]/30 to-transparent"></div>
        </div>
      </div>

      <div className={`${isReversed ? 'lg:order-1' : ''}`}>
        <span className="text-6xl font-bold text-[#2A2E35] mb-4 block">{step.number}</span>
        <h3 className="text-3xl md:text-4xl font-bold text-[#F5F7FA] mb-6 tracking-tight">
          {step.title}
        </h3>
        <p className="text-base text-[#F5F7FA]/70 mb-4 leading-relaxed">
          {step.description}
        </p>
        <p className="text-sm text-[#C9CDD3]/50 leading-relaxed">
          {step.subtitle}
        </p>
      </div>
    </div>
  );
}
